import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { supabase } from '@/lib/supabase';

const links = [
  { label: 'Bảng điều khiển', to: '/bang-dieu-khien', icon: 'ri-dashboard-3-line' },
  { label: 'Nạp tiền', to: '/nap-tien', icon: 'ri-arrow-down-circle-line' },
  { label: 'Rút tiền', to: '/rut-tien', icon: 'ri-arrow-up-circle-line' },
  { label: 'Giao dịch', to: '/giao-dich', icon: 'ri-exchange-line' },
  { label: 'Tài sản', to: '/tai-san', icon: 'ri-copper-coin-line' },
  { label: 'Hồ sơ', to: '/ho-so', icon: 'ri-user-3-line' },
  { label: 'Xác minh danh tính', to: '/xac-minh-danh-tinh', icon: 'ri-shield-check-line' },
  { label: 'Ngân hàng', to: '/ngan-hang', icon: 'ri-bank-line' },
  { label: 'Thông báo', to: '/thong-bao', icon: 'ri-notification-3-line' },
  { label: 'Hỗ trợ', to: '/lien-he', icon: 'ri-customer-service-2-line' },
];

export default function AccountLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('Người dùng');
  const [initials, setInitials] = useState('U');
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) return;

      const [{ data: profileRaw }, { data: kycRaw }] = await Promise.all([
        supabase.rpc('get_user_profile_json', { p_user_id: uid }),
        supabase.rpc('get_user_kyc_json', { p_user_id: uid }),
      ]);

      const profilesData = Array.isArray(profileRaw) ? profileRaw : JSON.parse((profileRaw as string) || '[]');
      const profileData = profilesData[0];
      const kycArr = Array.isArray(kycRaw) ? kycRaw : JSON.parse((kycRaw as string) || '[]');
      const kycData = kycArr[0];

      if (cancelled) return;

      if (profileData) {
        const name = profileData.full_name || profileData.username || profileData.email || 'Người dùng';
        setDisplayName(name);
        const parts = String(name).trim().split(/\s+/).filter(Boolean);
        let ini = 'U';
        if (parts.length >= 2) {
          ini = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        } else if (parts.length === 1) {
          ini = parts[0].slice(0, 2).toUpperCase();
        }
        setInitials(ini);
      }

      if (kycData && kycData.status === 'approved') {
        setIsVerified(true);
      }
    }
    loadProfile();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="fixed top-0 inset-x-0 z-40 h-16 bg-background-100 border-b border-background-200/60 flex items-center justify-between px-4 md:px-6">
        <Logo />
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center">
              <span className="text-sm font-bold text-primary-400">{initials}</span>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground-950 leading-none">{displayName}</p>
              <p className="text-xs text-foreground-600 mt-0.5">{isVerified ? 'Đã xác minh' : 'Chưa xác minh'}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="text-sm text-foreground-600 hover:text-primary-300 cursor-pointer whitespace-nowrap transition-colors">
            Đăng xuất
          </button>
        </div>
      </header>

      <div className="pt-16 flex">
        <aside className="hidden lg:block w-60 shrink-0 border-r border-background-200/60 bg-background-100 min-h-[calc(100vh-4rem)] sticky top-16 p-4">
          <nav className="space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    isActive ? 'bg-primary-500/15 text-primary-400' : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60'
                  }`
                }
              >
                <i className={`${l.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                {l.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="lg:hidden overflow-x-auto border-b border-background-200/60 bg-background-100 px-3 py-2 sticky top-16 z-30">
            <div className="flex gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer ${
                      isActive ? 'bg-primary-500/15 text-primary-400' : 'text-foreground-600'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </div>
          <main className="p-4 md:p-6">{children}</main>
        </div>
      </div>
    </div>
  );
}